import clsx from 'clsx';
import { useState } from 'react';

type TabSelectorVariant = 'primary' | 'secondary' | 'tertiary' | 'warning' | 'error';
type TabSelectorSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

const VARIANT_SELECTED_BG: Record<TabSelectorVariant, string> = {
  primary: 'var(--blue-color)',
  secondary: '#64748b',
  tertiary: 'var(--blue-color)',
  warning: '#d97706',
  error: '#dc2626',
};

const TRACK_SIZE_CLASS: Record<TabSelectorSize, string> = {
  '2xs': 'rounded-md p-0.5 gap-0.5',
  xs: 'rounded-lg p-0.5 gap-0.5',
  sm: 'rounded-xl p-0.5 gap-0.5',
  md: 'rounded-xl p-1 gap-1',
  lg: 'rounded-2xl p-1.5 gap-1.5',
};

const BUTTON_SIZE_CLASS: Record<TabSelectorSize, string> = {
  '2xs': 'rounded p-2 text-xs leading-tight font-semibold',
  xs: 'rounded-md p-2 text-xs leading-snug font-semibold',
  sm: 'rounded-lg px-4 py-2 text-xs font-semibold',
  md: 'rounded-lg px-6 py-2.5 text-sm font-semibold',
  lg: 'rounded-lg px-8 py-3 text-base font-semibold',
};

type TabSelectorProps = {
  options: { label: string; value: string }[];
  variant?: TabSelectorVariant;
  size?: TabSelectorSize;
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  justify?: 'center' | 'start' | 'end';
};

const TabSelector = ({
  className = '',
  justify = 'center',
  variant = 'primary',
  size = 'md',
  options,
  value: controlledValue,
  onChange,
}: TabSelectorProps) => {
  const [internalValue, setInternalValue] = useState(options[0].value);
  const selectedValue = controlledValue !== undefined ? controlledValue : internalValue;

  const handleTabClick = (value: string) => {
    setInternalValue(value);
    onChange(value);
  };

  const justifyClass =
    justify === 'start' ? 'justify-start' : justify === 'end' ? 'justify-end' : 'justify-center';

  const selectedBg = VARIANT_SELECTED_BG[variant] ?? VARIANT_SELECTED_BG.primary;

  return (
    <>
      <div className={clsx('flex', justifyClass)}>
        <div className={clsx('inline-flex bg-gray-100', TRACK_SIZE_CLASS[size], className)}>
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              className={clsx(
                'transition-all text-muted-foreground hover:text-foreground',
                BUTTON_SIZE_CLASS[size],
              )}
              onClick={() => handleTabClick(option.value)}
              style={{
                backgroundColor: selectedValue === option.value ? selectedBg : 'transparent',
                color: selectedValue === option.value ? 'white' : 'var(--gray-color)',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default TabSelector;
